import { Observable, BehaviorSubject, Subject, Subscription } from 'rxjs'
import {
  IAudioCaptureStrategy,
  AudioLevelInfo,
  AudioChunk,
  CaptureStatus,
  AudioCaptureError,
} from '../audio-capture-strategy.interface'
import { AudioStreamingService } from '../../audio-streaming.service'

/**
 * Cloud STT audio capture strategy
 *
 * Wrapper around AudioStreamingService for Cloud STT mode.
 * Streams PCM audio via WebSocket for server-side speech recognition.
 *
 * This strategy coordinates:
 * - AudioStreamingService: Provides PCM audio chunks via AudioWorklet and audio level monitoring
 * - WebSocket audio-stream events: Handled by consumers of getAudioChunk$()
 *
 * Note: AudioStreamingService already includes built-in audio level monitoring,
 * so we use that directly instead of AudioLevelMonitorService.
 *
 * Requirements:
 * - 6.3: Coordinate AudioStreamingService, WebSocket audio-stream events, turn lifecycle
 * - 2.5: Normalize output format (PCM ArrayBuffer chunks)
 * - 2.1: Provide unified interface for audio capture
 * - 11.2: Implement audio level monitoring and status management
 *
 * @example
 * ```typescript
 * const strategy = new CloudSttStrategy(audioStreamingService)
 * await strategy.initialize('device-id-123')
 * await strategy.start()
 *
 * strategy.getAudioChunk$().subscribe(chunk => {
 *   // Send PCM chunk to WebSocket server
 *   websocket.emit('audio-stream', chunk.data)
 * })
 *
 * await strategy.stop()
 * ```
 */
export class CloudSttStrategy implements IAudioCaptureStrategy {
  private audioLevelSubject = new Subject<AudioLevelInfo>()
  private audioChunkSubject = new Subject<AudioChunk>()
  private statusSubject = new BehaviorSubject<CaptureStatus>({
    isInitialized: false,
    isCapturing: false,
    deviceId: null,
  })
  private errorSubject = new Subject<AudioCaptureError>()

  private isInitialized = false
  private isCapturing = false
  private deviceId: string | null = null
  private chunkSequence = 0

  // Subscriptions for cleanup
  private audioChunkSubscription?: Subscription
  private audioLevelSubscription?: Subscription
  private statusSubscription?: Subscription

  constructor(private audioStreamingService: AudioStreamingService) {}

  /**
   * Initialize Cloud STT strategy with audio device
   *
   * Sets up AudioStreamingService with the specified device ID.
   * Must be called before start().
   *
   * @param deviceId - Optional audio input device ID
   * @throws AudioCaptureError if initialization fails
   */
  async initialize(deviceId?: string | null): Promise<void> {
    try {
      this.deviceId = deviceId ?? null

      // Initialize AudioStreamingService
      await this.audioStreamingService.initialize(this.deviceId)

      this.isInitialized = true
      this.chunkSequence = 0

      // Emit initialized status
      this.statusSubject.next({
        isInitialized: true,
        isCapturing: false,
        deviceId: this.deviceId,
      })

      // Subscribe to AudioStreamingService status for error detection
      this.statusSubscription = this.audioStreamingService.status$.subscribe(
        (status) => {
          if (status.error) {
            this.errorSubject.next({
              code: 'CAPTURE_FAILED',
              message: `Audio streaming error: ${status.error}`,
              canRetry: true,
              context: { deviceId: this.deviceId },
            })
          }
        },
      )
    } catch (error) {
      this.isInitialized = false
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'

      this.errorSubject.next({
        code: 'INITIALIZATION_FAILED',
        message: `Failed to initialize Cloud STT strategy: ${errorMessage}`,
        canRetry: true,
        context: {
          deviceId: this.deviceId,
          originalError: errorMessage,
        },
      })

      throw error
    }
  }

  /**
   * Start audio capture
   *
   * Begins streaming PCM audio chunks and monitoring audio levels.
   * Emits chunks via getAudioChunk$() and levels via getAudioLevel$().
   *
   * @throws AudioCaptureError if not initialized
   */
  async start(): Promise<void> {
    if (!this.isInitialized) {
      this.errorSubject.next({
        code: 'NOT_INITIALIZED',
        message:
          'Cannot start capture: strategy not initialized. Call initialize() first.',
        canRetry: true,
        context: {},
      })
      return
    }

    if (this.isCapturing) {
      return
    }

    // Start AudioStreamingService
    await this.audioStreamingService.startStreaming()

    // Subscribe to audio chunks from AudioStreamingService
    this.audioChunkSubscription =
      this.audioStreamingService.audioChunk$.subscribe((chunk) => {
        if (this.isCapturing) {
          // Convert to IAudioCaptureStrategy format
          const audioChunk: AudioChunk = {
            data: chunk.data, // ArrayBuffer (PCM format)
            format: 'pcm',
            sequence: chunk.sequence,
            timestamp: Date.now(),
            isComplete: false,
          }
          this.audioChunkSubject.next(audioChunk)
        }
      })

    // Subscribe to audio levels from AudioStreamingService
    // AudioStreamingService already has built-in audio level monitoring
    this.audioLevelSubscription =
      this.audioStreamingService.audioLevel$.subscribe((levelEvent) => {
        // Convert AudioStreamingService format to IAudioCaptureStrategy format
        const audioLevelInfo: AudioLevelInfo = {
          currentLevel: levelEvent.level,
          isDetectingVoice: levelEvent.isSpeaking,
          timestamp: Date.now(),
        }
        this.audioLevelSubject.next(audioLevelInfo)
      })

    this.isCapturing = true

    // Emit capturing status
    this.statusSubject.next({
      isInitialized: true,
      isCapturing: true,
      deviceId: this.deviceId,
    })
  }

  /**
   * Stop audio capture
   *
   * Stops streaming audio and releases active subscriptions.
   * Does not fully clean up - call initialize() again to restart.
   */
  async stop(): Promise<void> {
    if (!this.isCapturing) {
      return
    }

    // Stop AudioStreamingService
    this.audioStreamingService.stopStreaming()

    // Unsubscribe from streams
    this.audioChunkSubscription?.unsubscribe()
    this.audioLevelSubscription?.unsubscribe()

    this.isCapturing = false

    // Emit stopped status
    this.statusSubject.next({
      isInitialized: true,
      isCapturing: false,
      deviceId: this.deviceId,
    })
  }

  /**
   * Get real-time audio level stream
   *
   * Provides audio levels from AudioLevelMonitorService.
   * Emits continuously while capturing.
   *
   * @returns Observable stream of audio level information
   */
  getAudioLevel$(): Observable<AudioLevelInfo> {
    return this.audioLevelSubject.asObservable()
  }

  /**
   * Get PCM audio chunk stream
   *
   * Provides PCM audio data as ArrayBuffer chunks.
   * Format is normalized to 'pcm' for Cloud STT mode.
   * Consumers should send these chunks to WebSocket server.
   *
   * @returns Observable stream of PCM audio chunks
   */
  getAudioChunk$(): Observable<AudioChunk> {
    return this.audioChunkSubject.asObservable()
  }

  /**
   * Get capture status stream
   *
   * Emits status updates when initialization or capture state changes.
   *
   * @returns Observable stream of capture status
   */
  getStatus$(): Observable<CaptureStatus> {
    return this.statusSubject.asObservable()
  }

  /**
   * Get error stream
   *
   * Emits when errors occur during initialization or capture.
   * Check canRetry flag to determine recovery strategy.
   *
   * @returns Observable stream of capture errors
   */
  getError$(): Observable<AudioCaptureError> {
    return this.errorSubject.asObservable()
  }
}
